import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MapPin, CircleNotch } from "@phosphor-icons/react";

// Wiederverwendbare Adress-Autocomplete (OpenStreetMap/Photon via Backend-Proxy).
// onSelect liefert strukturierte Adresse inkl. Koordinaten.
export function AddressAutocomplete({ value, onChange, onSelect, placeholder, testid }) {
  const [q, setQ] = useState(value || "");
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const timer = useRef();
  const box = useRef();

  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onType = (val) => {
    setQ(val); onChange?.(val);
    clearTimeout(timer.current);
    if (!val || val.trim().length < 3) { setSugs([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true); setErr("");
      try {
        const { data } = await api.get(`/geo/search?q=${encodeURIComponent(val)}`);
        setSugs(data); setOpen(true);
        if (!data.length) setErr("Keine Treffer");
      } catch { setSugs([]); setErr("Adressdienst nicht erreichbar"); setOpen(true); }
      finally { setLoading(false); }
    }, 350);
  };

  const pick = (s) => { setQ(s.label); onChange?.(s.label); onSelect?.(s); setOpen(false); setSugs([]); };

  return (
    <div className="relative" ref={box}>
      <div className="relative">
        <Input data-testid={testid} value={q} autoComplete="off"
          placeholder={placeholder || "Adresse suchen (Straße, Nr., PLZ, Ort)…"}
          onChange={(e) => onType(e.target.value)}
          onFocus={() => sugs.length && setOpen(true)} />
        {loading && <CircleNotch size={15} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
      </div>
      {open && (sugs.length > 0 || err) && (
        <div data-testid={`${testid}-suggestions`} className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
          {sugs.length === 0 && err && <div className="px-3 py-2 text-sm text-muted-foreground">{err}</div>}
          {sugs.map((s, i) => (
            <button key={i} type="button" data-testid={`${testid}-option-${i}`} onClick={() => pick(s)}
              className="flex w-full items-start gap-2 border-b border-border/50 px-3 py-2 text-left text-sm last:border-0 hover:bg-accent">
              <MapPin size={14} weight="duotone" className="mt-0.5 shrink-0 text-primary" />
              <span className="min-w-0">{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
